import { useEffect, useRef } from 'react';

import { renderMarkdown } from '../../../common/components/markdown/markdown';
import { useRepositoryName } from '../../../global/contexts/lookups/lookups';
import { scopeText } from '../utils/version';

import '../styles/plans.css';

// What somebody reads before signing.
//
// APPROVING IS THE ONE ACTION IN THIS PANEL THAT CANNOT BE UNDONE. An approved
// version is fixed: it is not edited and it is not revoked by any route, and
// tiers are built from it. So the confirmation is not a "are you sure" — it is
// the exact version, the exact scope and the exact text that the signature will
// cover.
//
// AND IT IS FROZEN ON WHAT WAS READ. The version object comes in as a prop from
// the screen that loaded it; this component never refetches. A background
// refresh must not change what a person is about to sign between reading it and
// clicking — which is exactly the kind of thing that is invisible when it goes
// wrong.
//
// `version` is the adapted model (utils/version.js): the text is `content` and
// the scope is `scope`, both read from beside the raw `version` block.

export default function ApproveConfirm({ version, number, busy, onCancel, onConfirm }) {
    const repoName = useRepositoryName();
    const confirmRef = useRef(null);
    const openerRef = useRef(null);

    useEffect(() => {
        // Remember who had focus, take it, and give it back on the way out.
        openerRef.current = document.activeElement;
        confirmRef.current?.focus();

        const onKey = (event) => { if (event.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey);

        return () => {
            document.removeEventListener('keydown', onKey);
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
        };
    }, [onCancel]);

    return (
        <div className="plans__dialog" role="dialog" aria-modal="true" aria-labelledby="approve-title">
            <h3 className="plans__dialog-title" id="approve-title">
                Aprobar la versión {number}
            </h3>

            <dl className="plans__summary">
                <div>
                    <dt>Versión</dt>
                    <dd>{number}</dd>
                </div>
                <div>
                    <dt>Revisión</dt>
                    <dd>{version.revision}</dd>
                </div>
                <div>
                    <dt>Alcance</dt>
                    <dd>{scopeText(version.scope, repoName)}</dd>
                </div>
            </dl>

            <p className="plans__note">
                De acá van a salir los tiers. Una versión aprobada <strong>no se edita ni se
                revoca</strong>: corregirla es preparar la siguiente. Y los tiers que ya apuntan a
                otra versión no cambian de origen por esto.
            </p>

            <div className="plans__summary-content">
                <article className="md">{renderMarkdown(version.content)}</article>
            </div>

            <div className="plans__actions">
                <button type="button" className="plans__button" onClick={onCancel} disabled={busy}>
                    Cancelar
                </button>
                <button
                    ref={confirmRef}
                    type="button"
                    className="plans__button plans__button--primary"
                    onClick={onConfirm}
                    disabled={busy}
                >
                    {busy ? 'Aprobando...' : `Aprobar la versión ${number}`}
                </button>
            </div>
        </div>
    );
}
